"use client";

import { c as _c } from "react/compiler-runtime";
import { cn } from "@repo/design-system/utils/cn";
import { displayDate, isBeforeToday } from "@repo/design-system/utils/date-helpers";
import { combineEmploymentTypes } from "@repo/design-system/utils/helpers";
import { formatSalaryRow, getSalaryState } from "@repo/design-system/utils/salary-display";
import { Printer } from "lucide-react";
import { useEffect, useState } from "react";
import PrivacyNotice from "~/components/job-page/jobadtemplate/privacy-notice-eon";
import JobAdVideo from "~/components/job-page/jobadvideo/jobad-video-eon";
import { Button } from "~/components/ui/button-eon";
import { LocationDisplay } from "~/components/ui/location-display-eon";
import ShareDropdown from "~/components/ui/share-dropdown-eon";
import { WatchJobButton } from "~/components/ui/watch-job-button-eon";
import { toSimilarJob } from "~/lib/utils/to-similar-job";
import { useLanguage } from "~/translations/loader/use-language";
import type { JobadComponentProps } from "~/types/job";
const JOB_VIDEOS: Record<string, {
  thumbnail: string;
  video: string;
}> = {
  "498": {
    video: "sverige/498.mp4",
    thumbnail: "images/videos/sverige/thumbnail-498.webp"
  },
  "499": {
    video: "sverige/499.mp4",
    thumbnail: "images/videos/sverige/thumbnail-499.webp"
  }
};

// Splits the task HTML right before the contact block (lookahead keeps the
// matched line) so it can be rendered separately in the contact section.
export const SPLIT_REGEX = /(?=(?:<(?:b|strong)>\s*)?(?:För frågor om tjänsten|For questions regarding the position|For questions about the position, please contact:|För fackliga frågor|For questions about the position, please contact the recruiting manager:))/;
export default function JobAdTemplateEonSverige(t0) {
  const $ = _c(132);
  const {
    job
  } = t0;
  const t = useLanguage();
  const {
    content,
    data
  } = job;
  let t1;
  if ($[0] !== content.compensationObject || $[1] !== t) {
    const salaryState = getSalaryState(content.compensationObject);
    t1 = formatSalaryRow(salaryState, t, t.i18n);
    $[0] = content.compensationObject;
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const salaryRow = t1;
  const [newTask, setNewTask] = useState(content.task);
  let t2;
  let t3;
  if ($[3] !== content.task) {
    t2 = () => {
      const parser = new DOMParser();
      const doc = parser.parseFromString(content.task, "text/html");
      const paragraphs = Array.from(doc.querySelectorAll("p"));
      const inclusionParagraph = paragraphs.find(_temp);
      const benefitParagraph = paragraphs.find(_temp2);
      if (inclusionParagraph && benefitParagraph) {
        inclusionParagraph.remove();
        benefitParagraph.remove();
      }
      setNewTask(doc.body.innerHTML.split(SPLIT_REGEX)[0]);
    };
    t3 = [content.task];
    $[3] = content.task;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useEffect(t2, t3);
  let t4;
  if ($[6] !== data.title) {
    t4 = <h2 className="mb-4 font-bold text-4xl text-primary md:text-5xl">{data.title}</h2>;
    $[6] = data.title;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const t5 = !data.locations || data.locations.length === 1 ? `${t.jobad_locationDisplay_single}: ` : `${t.jobad_locationDisplay_multi}: `;
  let t6;
  if ($[8] !== t5) {
    t6 = <span className="font-medium" data-source="lc">{t5}</span>;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== job) {
    t7 = <span data-source="job"><LocationDisplay job={job} showAll={true} /></span>;
    $[10] = job;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t6 || $[13] !== t7) {
    t8 = <div className="mb-10 text-lg">{t6}{t7}</div>;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = cn("overflow-wrap-break-word print:break-inside-auto print:text-black", "[&_p+p]:mt-4", "[&_p:not(li>p)]:mb-4 [&_p:not(li>p)]:print:my-0 [&_p:not(li>p)]:print:p-0", "[&_h1]:mb-4 [&_h1]:print:my-0 [&_h1]:print:p-0", "[&_h2]:mb-4 [&_h2]:print:my-0 [&_h2]:print:p-0", "[&_h3]:mb-4 [&_h3]:print:my-0 [&_h3]:print:p-0", "[&_li]:mb-1.5", "[&_ul]:mt-4 [&_ul]:mb-8 [&_ul]:ml-6 [&_ul]:list-disc [&_ul]:print:my-0 [&_ul]:print:py-0");
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t.jobad_description_HL) {
    t10 = <h3 className="sr-only">{t.jobad_description_HL}</h3>;
    $[16] = t.jobad_description_HL;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== content.task || $[19] !== content.taskHL || $[20] !== newTask) {
    t11 = content.task && <div className="mt-6">{content.taskHL && <h3 className="font-bold" data-source="job">{content.taskHL}</h3>}<div dangerouslySetInnerHTML={{
        __html: newTask
      }} data-source="job" /></div>;
    $[18] = content.task;
    $[19] = content.taskHL;
    $[20] = newTask;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== content.profile || $[23] !== content.profileHL) {
    t12 = content.profile && <div className="mt-6">{content.profileHL && <h3 className="font-bold" data-source="job">{content.profileHL}</h3>}<div dangerouslySetInnerHTML={{
        __html: content.profile
      }} data-source="job" /></div>;
    $[22] = content.profile;
    $[23] = content.profileHL;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== content.offer || $[26] !== content.offerHL) {
    t13 = content.offer && <div className="mt-6">{content.offerHL && <h3 className="font-bold" data-source="job">{content.offerHL}</h3>}<div dangerouslySetInnerHTML={{
        __html: content.offer
      }} data-source="job" /></div>;
    $[25] = content.offer;
    $[26] = content.offerHL;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== t10 || $[29] !== t11 || $[30] !== t12 || $[31] !== t13) {
    t14 = <div className={t9}>{t10}{t11}{t12}{t13}</div>;
    $[28] = t10;
    $[29] = t11;
    $[30] = t12;
    $[31] = t13;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== data.jobNumber) {
    t15 = JOB_VIDEOS[data.jobNumber] && <JobAdVideo className="bg-card" thumbnailSrc={JOB_VIDEOS[data.jobNumber].thumbnail} videoUrl={JOB_VIDEOS[data.jobNumber].video} />;
    $[33] = data.jobNumber;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== t.jobad_details_HL) {
    t16 = <h3 className="mb-6 font-bold text-2xl" data-source="lc">{t.jobad_details_HL}:</h3>;
    $[35] = t.jobad_details_HL;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== t.jobad_details_contractType) {
    t17 = <th data-source="lc">{t.jobad_details_contractType}:</th>;
    $[37] = t.jobad_details_contractType;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== data.contract) {
    t18 = <td data-source="job">{data.contract}</td>;
    $[39] = data.contract;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  let t19;
  if ($[41] !== t17 || $[42] !== t18) {
    t19 = <tr>{t17}{t18}</tr>;
    $[41] = t17;
    $[42] = t18;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  let t20;
  if ($[44] !== t.jobad_details_employmentType) {
    t20 = <th data-source="lc">{t.jobad_details_employmentType}:</th>;
    $[44] = t.jobad_details_employmentType;
    $[45] = t20;
  } else {
    t20 = $[45];
  }
  let t21;
  if ($[46] !== data.employmentType || $[47] !== t) {
    t21 = combineEmploymentTypes(data.employmentType, t);
    $[46] = data.employmentType;
    $[47] = t;
    $[48] = t21;
  } else {
    t21 = $[48];
  }
  let t22;
  if ($[49] !== t21) {
    t22 = <td data-source="job">{t21}</td>;
    $[49] = t21;
    $[50] = t22;
  } else {
    t22 = $[50];
  }
  let t23;
  if ($[51] !== t20 || $[52] !== t22) {
    t23 = <tr>{t20}{t22}</tr>;
    $[51] = t20;
    $[52] = t22;
    $[53] = t23;
  } else {
    t23 = $[53];
  }
  let t24;
  if ($[54] !== t.jobad_details_remote) {
    t24 = <th data-source="lc">{t.jobad_details_remote}:</th>;
    $[54] = t.jobad_details_remote;
    $[55] = t24;
  } else {
    t24 = $[55];
  }
  let t25;
  if ($[56] !== data.remote) {
    t25 = <td data-source="job">{data.remote}</td>;
    $[56] = data.remote;
    $[57] = t25;
  } else {
    t25 = $[57];
  }
  let t26;
  if ($[58] !== t24 || $[59] !== t25) {
    t26 = <tr>{t24}{t25}</tr>;
    $[58] = t24;
    $[59] = t25;
    $[60] = t26;
  } else {
    t26 = $[60];
  }
  let t27;
  if ($[61] !== t.jobad_details_jobNumber) {
    t27 = <th data-source="lc">{t.jobad_details_jobNumber}:</th>;
    $[61] = t.jobad_details_jobNumber;
    $[62] = t27;
  } else {
    t27 = $[62];
  }
  let t28;
  if ($[63] !== data.jobNumber) {
    t28 = <td data-source="job">{data.jobNumber}</td>;
    $[63] = data.jobNumber;
    $[64] = t28;
  } else {
    t28 = $[64];
  }
  let t29;
  if ($[65] !== t27 || $[66] !== t28) {
    t29 = <tr>{t27}{t28}</tr>;
    $[65] = t27;
    $[66] = t28;
    $[67] = t29;
  } else {
    t29 = $[67];
  }
  let t30;
  if ($[68] !== salaryRow) {
    t30 = salaryRow && <tr><th data-source="lc">{salaryRow.label}:</th><td data-source="job">{salaryRow.value}</td></tr>;
    $[68] = salaryRow;
    $[69] = t30;
  } else {
    t30 = $[69];
  }
  let t31;
  if ($[70] !== data.applicationEnd || $[71] !== t) {
    t31 = data.applicationEnd && !isBeforeToday(data.applicationEnd) && <tr><th data-source="lc">{t.jobad_details_applicationDeadlineTitle}:</th><td data-source="job">{displayDate(data.applicationEnd.slice(0, 10), t)}</td></tr>;
    $[70] = data.applicationEnd;
    $[71] = t;
    $[72] = t31;
  } else {
    t31 = $[72];
  }
  let t32;
  if ($[73] !== t19 || $[74] !== t23 || $[75] !== t26 || $[76] !== t29 || $[77] !== t30 || $[78] !== t31) {
    t32 = <table className="wrap-break-word mb-8 w-full text-left md:w-2/3 lg:w-full lg:table-fixed"><tbody className="mb-4">{t19}{t23}{t26}{t29}{t30}{t31}</tbody></table>;
    $[73] = t19;
    $[74] = t23;
    $[75] = t26;
    $[76] = t29;
    $[77] = t30;
    $[78] = t31;
    $[79] = t32;
  } else {
    t32 = $[79];
  }
  let t33;
  if ($[80] !== salaryRow || $[81] !== t.salary_info_text_additional) {
    t33 = salaryRow && <p data-source="lc">{t.salary_info_text_additional}</p>;
    $[80] = salaryRow;
    $[81] = t.salary_info_text_additional;
    $[82] = t33;
  } else {
    t33 = $[82];
  }
  let t34;
  if ($[83] !== data.applicationEnd || $[84] !== t.sverige_applicationDeadline) {
    t34 = (!data.applicationEnd || isBeforeToday(data.applicationEnd)) && <p data-source="lc">{t.sverige_applicationDeadline}</p>;
    $[83] = data.applicationEnd;
    $[84] = t.sverige_applicationDeadline;
    $[85] = t34;
  } else {
    t34 = $[85];
  }
  let t35;
  if ($[86] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = <PrivacyNotice className="mt-4" />;
    $[86] = t35;
  } else {
    t35 = $[86];
  }
  let t36;
  if ($[87] !== t16 || $[88] !== t32 || $[89] !== t33 || $[90] !== t34) {
    t36 = <section className="rounded bg-card p-10 leading-6 [&_td]:py-2 [&_td]:pl-2 [&_th]:py-2 [&_th]:align-text-top [&_th]:font-normal">{t16}{t32}{t33}{t34}{t35}</section>;
    $[87] = t16;
    $[88] = t32;
    $[89] = t33;
    $[90] = t34;
    $[91] = t36;
  } else {
    t36 = $[91];
  }
  let t37;
  if ($[92] !== data) {
    t37 = toSimilarJob(data);
    $[92] = data;
    $[93] = t37;
  } else {
    t37 = $[93];
  }
  let t38;
  if ($[94] !== t37) {
    t38 = <WatchJobButton className="h-full w-full" jobData={t37} variant="purple" />;
    $[94] = t37;
    $[95] = t38;
  } else {
    t38 = $[95];
  }
  let t39;
  if ($[96] !== job) {
    t39 = <ShareDropdown className="h-full w-full" job={job} variant="purple" />;
    $[96] = job;
    $[97] = t39;
  } else {
    t39 = $[97];
  }
  let t40;
  if ($[98] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = <Printer className="size-6" />;
    $[98] = t40;
  } else {
    t40 = $[98];
  }
  let t41;
  if ($[99] !== t.jobad_print) {
    t41 = <Button aria-label={t.jobad_print} className="h-full w-full" onClick={_temp3} variant="purple">{t40}</Button>;
    $[99] = t.jobad_print;
    $[100] = t41;
  } else {
    t41 = $[100];
  }
  let t42;
  if ($[101] !== t38 || $[102] !== t39 || $[103] !== t41) {
    t42 = <section className="grid h-10 grid-flow-col grid-cols-3 gap-4 text-white">{t38}{t39}{t41}</section>;
    $[101] = t38;
    $[102] = t39;
    $[103] = t41;
    $[104] = t42;
  } else {
    t42 = $[104];
  }
  let t43;
  if ($[105] !== t.jobad_inclusionHL) {
    t43 = <p className="font-bold" data-source="lc">{t.jobad_inclusionHL}</p>;
    $[105] = t.jobad_inclusionHL;
    $[106] = t43;
  } else {
    t43 = $[106];
  }
  let t44;
  if ($[107] !== t.sverige_inclusion_text) {
    t44 = <p data-source="lc">{t.sverige_inclusion_text}</p>;
    $[107] = t.sverige_inclusion_text;
    $[108] = t44;
  } else {
    t44 = $[108];
  }
  let t45;
  if ($[109] !== t43 || $[110] !== t44) {
    t45 = <article className="rounded bg-card p-10">{t43}{t44}</article>;
    $[109] = t43;
    $[110] = t44;
    $[111] = t45;
  } else {
    t45 = $[111];
  }
  let t46;
  if ($[112] !== t.sverige_teaser_benefits) {
    t46 = <p className="font-bold" data-source="lc">{t.sverige_teaser_benefits}</p>;
    $[112] = t.sverige_teaser_benefits;
    $[113] = t46;
  } else {
    t46 = $[113];
  }
  let t47;
  if ($[114] !== t.sverige_benefits_text) {
    t47 = <p data-source="lc">{t.sverige_benefits_text}</p>;
    $[114] = t.sverige_benefits_text;
    $[115] = t47;
  } else {
    t47 = $[115];
  }
  let t48;
  if ($[116] !== t46 || $[117] !== t47) {
    t48 = <article className="rounded bg-primary p-10 text-white">{t46}{t47}</article>;
    $[116] = t46;
    $[117] = t47;
    $[118] = t48;
  } else {
    t48 = $[118];
  }
  let t49;
  if ($[119] !== t15 || $[120] !== t36 || $[121] !== t42 || $[122] !== t45 || $[123] !== t48) {
    t49 = <div className="flex flex-col gap-4">{t15}{t36}{t42}{t45}{t48}</div>;
    $[119] = t15;
    $[120] = t36;
    $[121] = t42;
    $[122] = t45;
    $[123] = t48;
    $[124] = t49;
  } else {
    t49 = $[124];
  }
  let t50;
  if ($[125] !== t14 || $[126] !== t49) {
    t50 = <div className="grid-cols-[minmax(0,1fr)_400px] gap-x-24 text-xl md:text-lg lg:grid xl:gap-x-32">{t14}{t49}</div>;
    $[125] = t14;
    $[126] = t49;
    $[127] = t50;
  } else {
    t50 = $[127];
  }
  let t51;
  if ($[128] !== t4 || $[129] !== t50 || $[130] !== t8) {
    t51 = <section className="outer-container py-container" id="job"><div className="inner-container">{t4}{t8}{t50}</div></section>;
    $[128] = t4;
    $[129] = t50;
    $[130] = t8;
    $[131] = t51;
  } else {
    t51 = $[131];
  }
  return t51;
}
function _temp3() {
  return window.print();
}
function _temp2(p_0) {
  return p_0.textContent?.includes("Vi arbetar f\xF6r ett mer");
}
function _temp(p) {
  return p.textContent?.includes("P\xE5 E.ON arbetar vi aktivt med inkludering");
}